import { useAutoAnimate } from '@formkit/auto-animate/react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import invariant from 'tiny-invariant';
import { match } from 'ts-pattern';

import type { CreditsafeStatus } from '~/api/clients/types';

import { useClientPeppolId, useCreditsafeRecord, useFetchVatInformation } from '~/api/clients';
import { Alert, Blocker, Button, Link, Text } from '~/components';
import { Field, Form } from '~/components/Form';
import { env } from '~/constants/env';
import { bypassBlocker } from '~/constants/router';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { isNotBlank } from '~/utils/string';
import { toast } from '~/utils/toast';

import type {
  BelgianOrganisationAddressFormType,
  CreateBelgianOrganisationAddressFormProps,
  CreateBelgianOrganisationConfirmationProps,
  SearchBeVatNumberFormType,
} from './types';

import { FormAddressCity } from '../../components/FormAddressCity';
import { FormAddressNumber } from '../../components/FormAddressNumber';
import { FormAddressStreet } from '../../components/FormAddressStreet';
import { FormAddressZipCode } from '../../components/FormAddressZipCode';
import {
  OrganisationInfoAddress,
  OrganisationInfoCard,
  OrganisationInfoCreditRating,
  OrganisationInfoGeneral,
  OrganisationInfoPeppol,
} from '../../components/OrganisationInfoCard';
import { useCreateClientContext } from '../context';
import { WizardFooter } from '../WizardFooter/WizardFooter';
import { VatCountryCodeSelect } from './components/VatCountryCodeSelect';
import { VatNumberInput } from './components/VatNumberInput';
import { useCreateOrganisationContext } from './context';
import { useGetDuplicateClient, useIsBlocked } from './hooks';
import { fullWidthStyle, gridStyle, inputRowStyle, inputStyle, searchRowStyle, stepStyle, textStyle } from './organisationStep.css';

export const CreateBelgianOrganisation = () => {
  const fetchVatInformation = useFetchVatInformation();
  const { organisation, resetClientContext, setEstablishmentUnits, setOrganisation } = useCreateClientContext();
  const { vatCountryCode } = useCreateOrganisationContext();

  invariant(vatCountryCode === 'BE', 'Expected selected country to be Belgium');

  const [autoAnimateRef] = useAutoAnimate();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { t } = useTranslation(['auth', 'clients', 'validation']);

  const { control, handleSubmit, setError } = useForm<SearchBeVatNumberFormType>({
    defaultValues: {
      enterpriseNumber: organisation?.vatNumber?.slice(2) ?? '',
    },
  });

  const isBlocked = useIsBlocked(control);

  const getDuplicateClient = useGetDuplicateClient();

  const onSubmit = handleSubmit((data) => {
    if (fetchVatInformation.isPending) return;

    fetchVatInformation.mutate(`${vatCountryCode}${data.enterpriseNumber.replaceAll(/\D/g, '')}`, {
      onSuccess: ({ vatActive, vatValid }) => {
        if (vatValid && vatActive) return;

        setError(
          'enterpriseNumber',
          !vatValid
            ? { type: 'validVat', message: t('validation:vatNumber.invalid') }
            : { type: 'activeVat', message: t('validation:vatNumber.inactive') },
        );
        fetchVatInformation.reset();
      },
      onError: () => {
        toast.danger({ description: t('clients:alerts.errorKboInformation') });
      },
    });
  });

  const vatInformation = fetchVatInformation.isSuccess && fetchVatInformation.data.source === 'KBO' ? fetchVatInformation.data : null;

  if (vatInformation) {
    invariant(vatInformation.vatValid, 'Expected valid VAT number');
    invariant(vatInformation.vatActive, 'Expected active VAT number');
  }

  const foundOrganisation = vatInformation && { vatNumber: vatInformation.vatNumber, companyName: vatInformation.companyName };
  const establishmentUnits = vatInformation?.establishmentUnits ?? [];
  const headquartersAddress = establishmentUnits.find(({ type }) => type === 'headquarters') || establishmentUnits[0];

  const resetSearchResult = () => {
    fetchVatInformation.reset();
    resetClientContext();
  };

  return (
    <div className={stepStyle} ref={autoAnimateRef}>
      {!foundOrganisation && (
        <>
          <Blocker isBlocked={isBlocked} message={t('clients:create.blocker')} />

          {env.KBO_UNAVAILABLE && (
            <Alert
              announce={false}
              icon="Warning"
              status="warning"
              title={t('auth:registrationPage.stepVatNumber.alert.kboNotAvailable.title')}
            >
              <Trans components={{ break: <br /> }} i18nKey="auth:registrationPage.stepVatNumber.alert.kboNotAvailable.description" />
            </Alert>
          )}
        </>
      )}

      <Form autoComplete="off" className={stepStyle} onSubmit={onSubmit}>
        <div className={searchRowStyle}>
          <VatCountryCodeSelect />

          <Controller
            control={control}
            name="enterpriseNumber"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
              <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                <Field.Label>{t('clients:fields.enterpriseNumber.label')}</Field.Label>

                <div className={inputRowStyle}>
                  <div className={inputStyle}>
                    <VatNumberInput countryCode={vatCountryCode} onBlur={onBlur} onChange={onChange} ref={ref} value={value} />
                  </div>

                  <Button icon="Search" loading={fetchVatInformation.isPending} type="submit">
                    {t('clients:create.company.searchCompanyInformation')}
                  </Button>
                </div>

                <Field.Error match={!!error}>
                  {error?.type === 'unique' ? <DuplicateClientError enterpriseNumber={value} /> : error?.message}
                </Field.Error>
              </Field.Root>
            )}
            rules={{
              required: { value: true, message: t('validation:required') },
              validate: {
                notBlank: (value) => isNotBlank(value) || t('validation:required'),
                unique: (value) => !getDuplicateClient(vatCountryCode, value),
                validFormat: (value) => /^[01][0-9]{9}$/.test(value.replaceAll(/\D/g, '')) || t('validation:vatNumber.belgianFormat'),
              },
            }}
          />
        </div>

        {!foundOrganisation && (
          <WizardFooter backTo={{ pathname: routes.createClient, search }}>
            <Button disabled icon="ArrowRight" iconPlacement="right">
              {t('clients:create.next')}
            </Button>
          </WizardFooter>
        )}
      </Form>

      {foundOrganisation &&
        (headquartersAddress ? (
          <CreateBelgianOrganisationConfirmation
            onCancel={resetSearchResult}
            onNext={() => {
              setOrganisation({ clientType: 'organisation', ...foundOrganisation, address: headquartersAddress });
              setEstablishmentUnits(establishmentUnits);
              navigate({ pathname: routes.createClientContactStep, search }, { state: bypassBlocker });
            }}
            organisation={{ ...foundOrganisation, address: headquartersAddress }}
          />
        ) : (
          <CreateBelgianOrganisationAddressForm
            onCancel={resetSearchResult}
            onNext={(address) => {
              setOrganisation({ clientType: 'organisation', ...foundOrganisation, address });
              navigate({ pathname: routes.createClientContactStep, search }, { state: bypassBlocker });
            }}
            organisation={foundOrganisation}
          />
        ))}
    </div>
  );
};

const DuplicateClientError = ({ enterpriseNumber }: { enterpriseNumber: string }) => {
  const getDuplicateClient = useGetDuplicateClient();

  const duplicateClient = getDuplicateClient('BE', enterpriseNumber);

  return (
    <Trans
      components={[
        // The client may already be gone; the message keeps its text and loses only its link.
        duplicateClient ? (
          <Link key={null} state={bypassBlocker} to={r(routes.createContact, { clientId: duplicateClient.id })} />
        ) : (
          <span key={null} />
        ),
      ]}
      i18nKey="validation:vatNumber.unique"
    />
  );
};

const CreateBelgianOrganisationConfirmation = ({ onCancel, onNext, organisation }: CreateBelgianOrganisationConfirmationProps) => {
  const { data: peppolInformation, status: peppolInformationStatus } = useClientPeppolId(organisation.vatNumber);
  const { data: creditsafeRecord, status: creditsafeStatus } = useCreditsafeRecord({
    countryCode: 'BE',
    vatNumber: organisation.vatNumber,
    companyName: organisation.companyName,
  });

  const { search } = useLocation();
  const { t } = useTranslation(['clients']);

  return (
    <div className={stepStyle}>
      <Blocker isBlocked message={t('clients:create.blocker')} />

      <OrganisationInfoCard onRemove={onCancel}>
        <OrganisationInfoGeneral companyName={organisation.companyName} otherNumber={null} vatNumber={organisation.vatNumber} />

        <OrganisationInfoAddress address={organisation.address} />

        <OrganisationInfoPeppol peppolId={peppolInformation?.peppolId ?? null} queryStatus={peppolInformationStatus} />

        <OrganisationInfoCreditRating
          creditsafeRecord={creditsafeRecord ?? null}
          creditsafeStatus={match(creditsafeStatus)
            .returnType<CreditsafeStatus>()
            .with('pending', () => 'NotFetched')
            .with('success', () => 'Success')
            .with('error', () => 'Error')
            .exhaustive()}
        />
      </OrganisationInfoCard>

      <Text className={textStyle}>{t('clients:create.company.companyInformationDisclaimer')}</Text>

      <WizardFooter backTo={{ pathname: routes.createClient, search }}>
        <Button icon="ArrowRight" iconPlacement="right" onClick={onNext}>
          {t('clients:create.next')}
        </Button>
      </WizardFooter>
    </div>
  );
};

const CreateBelgianOrganisationAddressForm = ({ onCancel, onNext, organisation }: CreateBelgianOrganisationAddressFormProps) => {
  const { data: creditsafeRecord, status: creditsafeStatus } = useCreditsafeRecord({
    countryCode: 'BE',
    vatNumber: organisation.vatNumber,
    companyName: organisation.companyName,
  });

  const { control, handleSubmit } = useForm<BelgianOrganisationAddressFormType>({
    defaultValues: {
      address: {
        street: '',
        number: '',
        zipCode: '',
        city: '',
      },
    },
  });

  const isBlocked = useIsBlocked(control);
  const { search } = useLocation();
  const { t } = useTranslation(['clients']);

  const onSubmit = handleSubmit((data) => onNext({ ...data.address, countryCode: 'BE' }));

  return (
    <div className={stepStyle}>
      <Blocker isBlocked={isBlocked} message={t('clients:create.blocker')} />

      <OrganisationInfoCard onRemove={onCancel}>
        <OrganisationInfoGeneral companyName={organisation.companyName} otherNumber={null} vatNumber={organisation.vatNumber} />
        <OrganisationInfoCreditRating
          creditsafeRecord={creditsafeRecord ?? null}
          creditsafeStatus={match(creditsafeStatus)
            .returnType<CreditsafeStatus>()
            .with('pending', () => 'NotFetched')
            .with('success', () => 'Success')
            .with('error', () => 'Error')
            .exhaustive()}
        />
      </OrganisationInfoCard>

      <Text>{t('clients:create.company.companyInformationDisclaimerWithoutAddress')}</Text>

      <Form autoComplete="off" className={stepStyle} onSubmit={onSubmit}>
        <div className={gridStyle}>
          <FormAddressStreet control={control} />
          <FormAddressNumber control={control} />
          <FormAddressZipCode control={control} />
          <FormAddressCity className={fullWidthStyle} control={control} />
        </div>

        <WizardFooter backTo={{ pathname: routes.createClient, search }}>
          <Button icon="ArrowRight" iconPlacement="right" type="submit">
            {t('clients:create.next')}
          </Button>
        </WizardFooter>
      </Form>
    </div>
  );
};
